import * as React from 'react';
const { useEffect, createRef, useCallback, Fragment } = React;
import api from '@seeneco-ui/api';
import { getPropInSafe } from '@seeneco-ui/core/utils';
import Box from '@seeneco-ui/core/components/box';
import Spinner from '@seeneco-ui/core/components/spinner';
import IconLink from '@seeneco-ui/core/components/icon-link';
import Button from '@seeneco-ui/core/components/button';
import TextField from '@seeneco-ui/core/components/text-field';
import {
	Form,
	FormRef,
	FormTextField,
	FormCopyToClipboardTextField,
	FormPasswordTextField,
} from '@seeneco-ui/core/components/form';
import AccountIcon from '@seeneco-ui/core/components/account-icon';

import { AuthorizeExternalSystemAccountOptions } from '../../../actions';
import { integrationHelpLinks } from '../../../../../shared/constants/cms';
import {
	NON_UPDATED_PASSWORD,
	IDENTITY_PROVIDER_ROLE,
	DATA_PROVIDER_ROLE,
} from '../../../../../shared/constants/specific';
import {
	Root,
	TitleLayout,
	Title,
	TitleInfoLayout,
	TitleBankLayout,
	TitleBankIconLayout,
	TitleBankName,
	TitleLinkLayout,
	SubmitButtonLayout,
} from './styled';


function getAuthorizationMethod(cms: CashManagementSystem, resMap: Record<string, RemoteExternalService>, ssiMap: Record<string, SubsystemInstance>) {
	const cmsGUID = cms.SubsystemInstanceGUID;
	const res = getPropInSafe(resMap, o => o[ssiMap[cmsGUID].ID]);
	const authMethodCode = getPropInSafe(res, o => o.Roles.find(role => role.CLASSIFIER === 'DATA_PROVIDER_ROLE')['AuthorizationMethodCode'], '');
	
	return {
		isExternalAccountID: authMethodCode === 'EXTERNAL_ACCOUNT_ID',
		isLoginPassword: authMethodCode === 'LOGIN_PASSWORD',
		isOAuthToken: authMethodCode === 'OAUTH_TOKEN'
	};
}

function buildExternalSystemAccount(externalSSInstanceID: number, name: string, login?: string, password?: string) {
	const account = {
		...new api.platform.security.package.ExternalSystemAccount(),
		ExternalSSInstanceID: externalSSInstanceID,
		Name: name,
	};

	if (login && password) {
		account.AdminUser = login;
		account.AdminPassword = password;
	}

	return account;
}

type UpdateExternalSystemAccountFormProps = {
	type: 'ADD' | 'EDIT';
	isFetching: boolean;
	account: ExternalSystemAccount;
	cms: CashManagementSystem;
	resMap: Record<string, RemoteExternalService>;
	ssiMap: Record<string, SubsystemInstance>;
	teList: Array<CounterpartyBrief>;
	addExternalSystemAccount: (account: ExternalSystemAccount) => void;
	addExternalSystemAccountAsPromise: (account: ExternalSystemAccount) => Promise<ExternalSystemAccount>;
	changeExternalSystemAccount: (account: ExternalSystemAccount) => void;
	authorizeExternalSystemAccount: (options: AuthorizeExternalSystemAccountOptions) => void;
	onSetFormHeader: (header: React.ReactNode) => void;
	onSetFormControls: (controls: React.ReactNode) => void;
	onClose: () => void;
};

const UpdateExternalSystemAccountForm: React.SFC<UpdateExternalSystemAccountFormProps> = (props) => {
	const {
		isFetching,
		type,
		account,
		cms,
		resMap,
		ssiMap,
		teList,
		onSetFormHeader,
		onSetFormControls,
		onClose,
		authorizeExternalSystemAccount,
		addExternalSystemAccount,
		addExternalSystemAccountAsPromise,
		changeExternalSystemAccount,
	} = props;
	const isAdd = type === 'ADD';
	const isEdit = type === 'EDIT';
	const formRef = createRef<FormRef>();

	useEffect(() => {
		setFormHeader();
		setFormControls();
	});

	const setFormHeader = () => {
		const title = type === 'ADD' ? 'Добавление учетной записи' : 'Изменение учетной записи';
		const bankName = cms ? cms.Name  : '';

		onSetFormHeader(
			<TitleLayout>
				<Title>{title}</Title>
				<TitleInfoLayout>
					<TitleBankLayout>
						<TitleBankIconLayout>
							<AccountIcon
								isBank
								bankName={bankName}
								width={30}
								height={30}
							/>
						</TitleBankIconLayout>
						<TitleBankName>
							{bankName}
							<TitleLinkLayout>
								<IconLink
									to={integrationHelpLinks[cms.SubsystemInstanceGUID]}
									rel='noopener'
									target='_blank'
									rightIconType='link-external'
									iconSize={7}
									iconColor='muted'
									color='muted'>
									Как подключить?
								</IconLink>
							</TitleLinkLayout>
						</TitleBankName>
					</TitleBankLayout>
				</TitleInfoLayout>
			</TitleLayout>
		);
	};

	const setFormControls = () => {
		const label = type === 'ADD' ? 'Добавить' : 'Сохранить';

		onSetFormControls(
			<React.Fragment>
				<Button onClick={handleInitSubmitForm}>
					{label}
				</Button>
				<Button color='default' onClick={onClose}>
					Отмена
				</Button>
			</React.Fragment>
		);
	};

	const handleInitSubmitForm = () => formRef.current.submitForm();
	const handleInitSubmitFormWithAuthorize = () => formRef.current.submitForm(true);

	const handleAddAccountWithExternalAccountID = React.useCallback((accountName: string) => {
		const cmsGUID = cms.SubsystemInstanceGUID;
		const ssi = ssiMap[cmsGUID];
		const externalSystemAccount = buildExternalSystemAccount(ssi.ID, accountName);

		addExternalSystemAccount(externalSystemAccount);
		onClose();
	}, []);

	const handleAddAccountWithLoginPassword =  useCallback((accountName: string, login: string, password: string) => {
		const cmsGUID = cms.SubsystemInstanceGUID;
		const ssi = ssiMap[cmsGUID];
		const externalSystemAccount = buildExternalSystemAccount(ssi.ID, accountName, login, password);

		addExternalSystemAccount(externalSystemAccount);
		onClose();
	}, []);

	const handleAddAcountWithOAuthToken =  useCallback((accountName: string, withAuthorize: boolean = false) => {
		const cmsGUID = cms.SubsystemInstanceGUID;
		const ssi = ssiMap[cmsGUID];
		const externalSystemAccount = buildExternalSystemAccount(ssi.ID, accountName);

		withAuthorize
			? addExternalSystemAccountAsPromise(externalSystemAccount).then(account => authorizeExternalSystemAccount({
				account,
				ssi,
			}))
			: addExternalSystemAccount(externalSystemAccount);
		onClose();
	}, []);

	const handleSubmitForm = useCallback((formObject: ExternalSystemAccount, creatingObject, withAuthorize: boolean = false) => {
		const cmsGUID = cms.SubsystemInstanceGUID;
		const ssi = ssiMap[cmsGUID];
		const {
			isExternalAccountID,
			isLoginPassword,
			isOAuthToken
		} = getAuthorizationMethod(cms, resMap, ssiMap);

		if (isAdd) {
			isExternalAccountID && handleAddAccountWithExternalAccountID(formObject.Name);
			isLoginPassword && handleAddAccountWithLoginPassword(formObject.Name, formObject.AdminUser, formObject.AdminPassword);
			isOAuthToken && handleAddAcountWithOAuthToken(formObject.Name, withAuthorize);
		} else if (isEdit) {
			if (isOAuthToken && withAuthorize) {
				return authorizeExternalSystemAccount({
					account: formObject,
					ssi,
				});
			}

			if (isLoginPassword) {
				if (formObject.AdminPassword === NON_UPDATED_PASSWORD) {
					formObject.AdminPassword = '';
				}
			} else {
				formObject.AdminPassword = '';
			}

			changeExternalSystemAccount(formObject);
		}

		onClose();
	}, []);

	const renderForm = () => {
		const [firstTenantEntity] = teList;
		const servisesMap = {
			[IDENTITY_PROVIDER_ROLE]: 'Удостоверяющий центр при аутентификации по SSO',
			[DATA_PROVIDER_ROLE]: 'Провайдер данных'
		};
		const cmsGUID = cms.SubsystemInstanceGUID;
		const res = getPropInSafe(resMap, o => o[ssiMap[cmsGUID].ID]);
		const servisesValue = res.Roles
			? res.Roles.reduce((acc, role, idx, arr) => acc + servisesMap[role.CLASSIFIER] + (idx === arr.length - 1 ? ' ' : ', '), '')
			: '';
		const { isLoginPassword, isOAuthToken } = getAuthorizationMethod(cms, resMap, ssiMap);
		const needRenderIDFiled = isEdit && !!account.GUID;
		const withAuthorize = isOAuthToken;

		account.Name = isAdd
			? firstTenantEntity
				? firstTenantEntity.Name || firstTenantEntity.NameEng
				: ''
			: account.Name;

		return (
			<Form
				ref={formRef as React.LegacyRef<any>}
				formObject={account}
				isFetching={isFetching}
				onSubmit={handleSubmitForm}>
				<FormTextField
					name='Name'
					label='Имя учетной записи'
					placeholder='ООО «Ромашка»'
					required
					fullWidth
				/>
				{
					needRenderIDFiled &&
					<FormCopyToClipboardTextField
						name='GUID'
						label='Идентификатор учетной записи'
						fullWidth
						disabled
					/>
				}
				<TextField
					label='Предоставляемые услуги'
					value={servisesValue}
					multiline
					readonly
					fullWidth
				/>
				{
					withAuthorize &&
					<TextField
						label='Статус учетной записи'
						value={account.Validated ? 'Действующая' : 'Неавторизованная'}
						readonly
						fullWidth
					/>
				}
				{
					isEdit &&
					<TextField
						label='Срок действия'
						value={account.ValidTo}
						readonly
						fullWidth
					/>
				}
				{
					isLoginPassword &&
					<Fragment>
						<FormTextField
							name='AdminUser'
							label='Имя пользователя'
							fullWidth
							required
						/>	
						<FormPasswordTextField
							name='AdminPassword'
							label='Пароль'
							fullWidth
							required
						/>
					</Fragment>
				}
				{
					withAuthorize &&
					<SubmitButtonLayout>
						<Button onClick={handleInitSubmitFormWithAuthorize}>
							Авторизовать
						</Button>
					</SubmitButtonLayout>
				}
			</Form>
		);
	};

	if (isFetching) {
		return (
			<Box
				width='100%'
				minHeight='200px'
				display='flex'
				alignItems='center'
				justifyContent='center'>
				<Spinner />
			</Box>
		);
	}

	return (
		<Root>
			{renderForm()}
		</Root>
	);
}

export default UpdateExternalSystemAccountForm;
